import React, { useState, useRef, useEffect } from 'react';

const CommandTooltip = ({ command, children, searchQuery, isLocked = false }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const targetRef = useRef(null);
  const tooltipRef = useRef(null);

  useEffect(() => {
    if (!showTooltip || !targetRef.current) return;

    const updatePosition = () => {
      const rect = targetRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current?.getBoundingClientRect();
      
      // Position tooltip above or below the target
      const spaceAbove = rect.top;
      const spaceBelow = window.innerHeight - rect.bottom;
      const tooltipHeight = tooltipRect?.height || 200;
      
      let top = 0;
      let left = rect.left;
      
      if (spaceBelow >= tooltipHeight) {
        // Show below
        top = rect.bottom + 8;
      } else if (spaceAbove >= tooltipHeight) {
        // Show above
        top = rect.top - tooltipHeight - 8;
      } else {
        // Center vertically if neither fits well
        top = rect.top + (rect.height / 2) - (tooltipHeight / 2);
      }

      // Adjust horizontal position to stay in viewport
      const tooltipWidth = tooltipRect?.width || 400;
      if (left + tooltipWidth > window.innerWidth) {
        left = window.innerWidth - tooltipWidth - 16;
      }
      if (left < 16) {
        left = 16;
      }

      setTooltipPosition({ top, left });
    };

    updatePosition();
    
    // Update on scroll/resize
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [showTooltip]);

  const handleMouseEnter = () => {
    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  return (
    <>
      <div
        ref={targetRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>
      {showTooltip && (
        <div
          ref={tooltipRef}
          className={`baiql-command-tooltip ${isLocked ? 'locked' : ''}`}
          style={{
            position: 'fixed',
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
            zIndex: 10000
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div className="baiql-command-tooltip-header">
            <code className="baiql-command-tooltip-name">{command.name}</code>
            {isLocked && <span className="baiql-command-tooltip-locked">🔒</span>}
          </div>
          <div className="baiql-command-tooltip-syntax">
            <strong>Syntax:</strong>
            <code>{command.syntax}</code>
          </div>
          <div className="baiql-command-tooltip-desc">
            {command.quickDesc}
          </div>
          {isLocked && (
            <div className="baiql-command-tooltip-upgrade">
              Requires {(command.tier || 'pro').toUpperCase()} tier
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default CommandTooltip;

